// Full-screen search overlay with instant results
import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Clock, TrendingUp, Sparkles, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearch, getRecentSearches, addRecentSearch, clearRecentSearches, SearchProduct } from '../../hooks/useSearch';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery?: string;
}

const POPULAR_SEARCHES = [
  'Dresses', 'Denim', 'Jumpsuit', 'Silk', 'Linen',
  'Blazer', 'Cashmere', 'Summer', 'Evening Wear',
];

export function SearchOverlay({ isOpen, onClose, initialQuery = '' }: SearchOverlayProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { searchQuery, setSearchQuery, results, loading, isAlgoliaEnabled } = useSearch({
    debounceMs: 250,
    hitsPerPage: 6,
  });

  const recentSearches = getRecentSearches();

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setSearchQuery(initialQuery);
      // Small delay to ensure overlay is visible before focusing
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setSearchQuery('');
    }
  }, [isOpen, initialQuery, setSearchQuery]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSearch = useCallback((query: string) => {
    if (!query.trim()) return;
    addRecentSearch(query.trim());
    onClose();
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  }, [navigate, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchQuery);
  };

  const handleProductClick = (product: SearchProduct) => {
    addRecentSearch(searchQuery);
    onClose();
    navigate(`/product/${product.id}`);
  };

  const handleSuggestionClick = (suggestion: string) => {
    addRecentSearch(suggestion);
    onClose();
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 bg-white"
        >
          {/* Search Header */}
          <div className="border-b border-gray-100">
            <div className="max-w-3xl mx-auto px-4 py-3">
              <form onSubmit={handleSubmit} className="flex items-center gap-3">
                <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products, brands, styles..."
                  className="flex-1 text-lg text-heading outline-none placeholder:text-gray-400 bg-transparent"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {loading && (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin flex-shrink-0" />
                )}
                {searchQuery && !loading && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm font-medium text-body hover:text-heading transition-colors ml-1"
                >
                  Cancel
                </button>
              </form>
            </div>
          </div>

          {/* Search Content */}
          <div className="max-w-3xl mx-auto px-4 py-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 60px)' }}>
            {/* ─── Instant Results ─── */}
            {searchQuery.trim() && results && results.hits.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-body uppercase tracking-wider">
                    {results.totalHits} result{results.totalHits !== 1 ? 's' : ''}
                    <span className="text-gray-400 ml-1">
                      ({results.processingTimeMs.toFixed(0)}ms)
                    </span>
                  </p>
                  <button
                    onClick={() => handleSearch(searchQuery)}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-hover transition-colors"
                  >
                    View all <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
                
                <div className="space-y-1">
                  {results.hits.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductClick(product)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left"
                    >
                      {/* Product Image */}
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        {(product.imageUrl || product.images?.[0]) && (
                          <img
                            src={product.imageUrl || product.images?.[0]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      
                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-medium text-primary uppercase tracking-wider">
                          {product.brand}
                        </p>
                        <p className="text-sm font-medium text-heading truncate">
                          {product.name}
                        </p>
                        {product.category && (
                          <p className="text-xs text-body mt-0.5">{product.category}</p>
                        )}
                      </div>
                      
                      {/* Price */}
                      <p className="text-sm font-medium text-heading flex-shrink-0">
                        ${product.price}
                      </p>
                    </button>
                  ))}
                </div>

                {/* View All Button */}
                {results.totalHits > results.hits.length && (
                  <button
                    onClick={() => handleSearch(searchQuery)}
                    className="w-full mt-3 py-3 text-sm font-medium text-primary border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    View all {results.totalHits} results
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}

                {/* Category Quick Filters from Facets */}
                {results.facets && results.facets.categories.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-body uppercase tracking-wider mb-2">
                      Categories
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {results.facets.categories.slice(0, 6).map(({ value, count }) => (
                        <button
                          key={value}
                          onClick={() => {
                            addRecentSearch(searchQuery);
                            onClose();
                            navigate(`/search?q=${encodeURIComponent(searchQuery)}&category=${encodeURIComponent(value)}`);
                          }}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs font-medium text-heading transition-colors"
                        >
                          {value} ({count})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ─── No Results ─── */}
            {searchQuery.trim() && results && results.hits.length === 0 && !loading && (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-serif text-heading mb-1">No results found</p>
                <p className="text-sm text-body mb-6">
                  Try different keywords or browse our categories
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {POPULAR_SEARCHES.slice(0, 5).map((term) => (
                    <button
                      key={term}
                      onClick={() => handleSuggestionClick(term)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium text-heading transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ─── Default State (no query) ─── */}
            {!searchQuery.trim() && (
              <div>
                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <p className="text-xs font-medium text-body uppercase tracking-wider">Recent</p>
                      </div>
                      <button
                        onClick={() => {
                          clearRecentSearches();
                          // Force re-render
                          setSearchQuery(' ');
                          setTimeout(() => setSearchQuery(''), 0);
                        }}
                        className="text-xs text-body hover:text-heading transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="space-y-0.5">
                      {recentSearches.map((term) => (
                        <button
                          key={term}
                          onClick={() => handleSuggestionClick(term)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                        >
                          <Clock className="h-4 w-4 text-gray-300 flex-shrink-0" />
                          <span className="text-sm text-heading">{term}</span>
                          <ArrowRight className="h-3.5 w-3.5 text-gray-300 ml-auto" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular Searches */}
                <div className="mb-6">
                  <div className="flex items-center gap-1.5 mb-3">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    <p className="text-xs font-medium text-body uppercase tracking-wider">Trending</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_SEARCHES.map((term) => (
                      <button
                        key={term}
                        onClick={() => handleSuggestionClick(term)}
                        className="px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full text-sm font-medium text-heading transition-colors"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Browse Categories */}
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Sparkles className="h-4 w-4 text-gray-400" />
                    <p className="text-xs font-medium text-body uppercase tracking-wider">Browse</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {['Dresses', 'Tops', 'Bottoms', 'Outerwear', 'Swimwear', 'Accessories'].map((category) => (
                      <button
                        key={category}
                        onClick={() => {
                          onClose();
                          navigate(`/search?category=${encodeURIComponent(category)}`);
                        }}
                        className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <span className="text-sm font-medium text-heading">{category}</span>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Powered by badge */}
            {isAlgoliaEnabled && (
              <div className="mt-8 pb-4 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                  Search powered by Algolia
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
